import {
  Component,
  computed,
  effect,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  SecurityContext,
} from '@angular/core';
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';
import { DomSanitizer } from '@angular/platform-browser';
import { FmntsCommonModule } from '@fmnts/common';
import { Logger, logger } from '@fmnts/common/log';
import { FmntsProgressIndicatorModule } from '@fmnts/components/progress-indicator';
import { fromPortMessage } from '@fmnts/core/rxjs';
import { Tamaro } from '@formunauts/shared/vendors/raisenow/domain';
import { patchState, signalState } from '@ngrx/signals';
import * as Sentry from '@sentry/angular';
import * as Data from 'effect/Data';
import * as Fn from 'effect/Function';
import * as O from 'effect/Option';
import {
  catchError,
  EMPTY,
  filter,
  finalize,
  map,
  merge,
  ReplaySubject,
  startWith,
  Subject,
  takeUntil,
  tap,
} from 'rxjs';
import { FMNTS_RAISENOW_TAMARO_HOST_OPTIONS } from './raisenow-integration.tokens';

export enum State {
  /** Not yet initialized. */
  Uninitialized,
  /** Content is ready. */
  Ready,
  /** Validation or Payment is in progress. */
  InProgress,
  /** Indicates whether payment was successful. */
  Done,
  /** Some error occured. */
  Failed,
}

const SENTRY_TAMARO_CATEGORY = 'rnw.tamaro';
enum SentryTamaroTag {
  Instance = 'rnw.tamaro.instance',
}

const TamaroState = Data.taggedEnum<
  | {
      readonly _tag: 'TamaroUninitialized';
    }
  | {
      readonly _tag: 'TamaroReady';
    }
  | {
      readonly _tag: 'TamaroInProgress';
    }
  | {
      readonly _tag: 'TamaroDone';
    }
  | {
      readonly _tag: 'TamaroFailed';
      readonly reason: unknown;
    }
>();
const {
  TamaroUninitialized,
  TamaroInProgress,
  TamaroReady,
  TamaroDone,
  TamaroFailed,
  $match,
  $is,
} = TamaroState;

export type TamaroState = Data.TaggedEnum<{
  TamaroUninitialized: NonNullable<void>;
  TamaroInProgress: NonNullable<void>;
  TamaroReady: NonNullable<void>;
  TamaroDone: NonNullable<void>;
  TamaroFailed: { readonly reason: unknown };
}>;

const toState = $match({
  TamaroUninitialized: () => State.Uninitialized,
  TamaroReady: () => State.Ready,
  TamaroInProgress: () => State.InProgress,
  TamaroDone: () => State.Done,
  TamaroFailed: () => State.Failed,
});

const isInitializing = $is('TamaroUninitialized');

/**
 * IFrame component, to host the new RaiseNow payment platform Tamaro.
 */
@Component({
  selector: 'raisenow-tamaro-iframe',
  templateUrl: './raisenow-tamaro-iframe.component.html',
  styleUrls: ['./raisenow-tamaro-iframe.component.scss'],
  standalone: true,
  imports: [FmntsProgressIndicatorModule, FmntsCommonModule],
})
export class RaiseNowTamaroIframeComponent implements OnInit, OnDestroy {
  protected readonly cfg = inject(FMNTS_RAISENOW_TAMARO_HOST_OPTIONS);
  readonly #sanitizer = inject(DomSanitizer);
  readonly #logs: Readonly<Record<Tamaro.MessageSource, Logger>> = {
    [Tamaro.MessageSource.Host]: logger({
      name: Tamaro.MessageSource.Host,
      category: SENTRY_TAMARO_CATEGORY,
    }),
    [Tamaro.MessageSource.Iframe]: logger({
      name: Tamaro.MessageSource.Iframe,
      category: SENTRY_TAMARO_CATEGORY,
    }),
    [Tamaro.MessageSource.Tamaro]: logger({
      name: Tamaro.MessageSource.Tamaro,
      category: SENTRY_TAMARO_CATEGORY,
    }),
  };

  /** Tamaro widget config. */
  readonly config = input.required<Readonly<Tamaro.WidgetConfig>>();
  /**
   * Tag of the raisenow tamaro instance to be used.
   */
  readonly instance = input.required<string>();

  /**
   * Configuration for the tamaro widget to be transfered to the iframe.
   */
  protected readonly widgetConfig = computed((): Tamaro.IframeConfig => {
    const tamaroConfig = this.config();
    const tamaroInstance = Fn.pipe(
      this.instance(),
      // Try to sanitize the passed raisenow tamaro instance, using `URL` context
      // since `RESOURCE_URL` can't be sanitized.
      // https://angular.io/guide/security#sanitization-and-security-contexts
      (instance) => this.#sanitizer.sanitize(SecurityContext.URL, instance),
      O.fromNullable,
      O.getOrThrow,
    );

    return {
      tamaroInstance,
      tamaroConfig,
      cleanupSession: this.cfg.cleanupSession,
    };
  });

  readonly #store = signalState<TamaroState>(TamaroUninitialized());

  /** Emits the state that the payment process is currently in. */
  readonly state = computed(() => toState(this.#store()));
  readonly stateChange = output<State>();
  readonly #isFinalState = computed(() =>
    [State.Done, State.Failed].includes(this.state()),
  );

  /** Emits when payment / subscription was successfully created. */
  readonly success = output<void>();
  /** Emits when payment / subscription could not be set up. */
  readonly failed = output<void>();

  /** Subject for receiving messages from iframe / tamaro. */
  readonly #msgIn$ = new Subject<Tamaro.MessageToHost>();
  /** Subject for sending messages to iframe / tamaro. */
  readonly #msgOut$ = new Subject<Tamaro.MessageFromHost>();

  readonly #destroyed$ = new ReplaySubject<boolean>(1);
  /** Once set to true, the iframe should be destroyed  */
  readonly #frameDestroyed$ = new ReplaySubject<true>(1);
  /** Emits immediately with whether the iframe should be available. */
  protected showFrame = toSignal(
    this.#frameDestroyed$.pipe(
      startWith(false),
      map((v) => !v),
    ),
    { requireSync: true },
  );

  /**
   * Emits `true`, once the payment process has a final result.
   * In this case the modal must be opened again.
   */
  readonly #finalState$ = toObservable(this.#isFinalState).pipe(
    filter((isFinal) => isFinal),
  );

  /** `true` if the iframe is initializing. */
  protected readonly isInitializing = computed(() =>
    isInitializing(this.state()),
  );

  constructor() {
    effect(() => {
      this.#emitStateChange(this.state());
    });

    // Whenever the widget config changes, we reset the state to uninitialized.
    // This is necessary to ensure that the iframe is reloaded with the new config.
    toObservable(this.widgetConfig)
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: () => {
          patchState(this.#store, TamaroUninitialized());
        },
      });

    // Setup listening for messages
    this.#msgIn$
      .pipe(
        catchError((e) => {
          Sentry.captureException(e);
          patchState(this.#store, TamaroFailed({ reason: e }));
          this.#destroyFrame();
          return EMPTY;
        }),
        takeUntilDestroyed(),
      )
      .subscribe((msg) => {
        this.#handleMessage(msg);
      });
  }

  ngOnInit(): void {
    this.#setupSentryScope();
  }

  ngOnDestroy(): void {
    this.#cleanupSentryScope();
    this.#destroyFrame();
    this.#destroyed$.next(true);
    this.#destroyed$.complete();
  }

  /**
   * Starts tamaro payment.
   */
  startPayment(): void {
    this.#sendMessage(
      Tamaro.MessageType.Action,
      Tamaro.IframeAction.ValidateAndSend,
    );
  }

  /**
   * Send the tamaro-config to the Tamaro-IFrame
   * and wait for message
   */
  protected _onIframeLoaded(
    tamaro: HTMLIFrameElement,
    config: Tamaro.IframeConfig,
  ): void {
    if (!tamaro.contentWindow) {
      this.#msgIn$.error(
        new Tamaro.HostError({ reason: 'Could not initialize Tamaro Widget' }),
      );
      return;
    }

    // Setup message channel between here and iframe. We'll listen on
    // port1 and transfer port2 to the iframe.
    const channel = new MessageChannel();

    fromPortMessage<Tamaro.MessageToHost>(channel.port1)
      .pipe(
        tap((msg) => {
          this.#logs[msg.source].info(`${msg.type} - ${msg.name}`);
          if (msg.type === 'error') {
            throw new Tamaro.TamaroError(msg.source, msg.name);
          }
        }),
        takeUntil(merge(this.#finalState$, this.#destroyed$)),
        finalize(() => {
          channel.port1.close();
          channel.port2.close();
        }),
      )
      .subscribe({
        next: (msg) => {
          this.#msgIn$.next(msg);
        },
        error: (e) => {
          this.#msgIn$.error(e);
        },
      });

    // Additionally, when a message is emitted on the `msgOut$` stream,
    // it should be sent to the iframe.
    this.#msgOut$
      .pipe(takeUntil(merge(this.#finalState$, this.#destroyed$)))
      .subscribe((msg) => {
        channel.port1.postMessage(msg);
      });

    tamaro.contentWindow.postMessage(
      config,
      '/', // restricts message to same-origin targets only
      [channel.port2],
    );
  }

  protected _onIframeFailed(): void {
    this.#msgIn$.error(
      new Tamaro.HostError({ reason: 'Error loading iframe' }),
    );
  }

  /**
   * Sends an action message.
   *
   * @param type Message type
   * @param action Action of the message
   */
  #sendMessage(
    type: Tamaro.MessageType.Action,
    action: Tamaro.IframeAction,
  ): void;
  #sendMessage(type: Tamaro.MessageType, name: Tamaro.Message['name']): void {
    this.#msgOut$.next({
      name,
      type,
      source: Tamaro.MessageSource.Host,
    });
  }

  #handleMessage(msg: Tamaro.MessageToHost) {
    switch (msg.type) {
      case Tamaro.MessageType.Event:
        return this.#handleEventMessage(msg);
      case Tamaro.MessageType.Result:
        return this.#handleResultMessage(msg);
    }
  }

  #handleEventMessage(msg: Tamaro.MessageToHost) {
    switch (msg.name) {
      case Tamaro.TamaroEvent.AfterRender:
      case Tamaro.TamaroEvent.PaymentValidateError:
        patchState(this.#store, TamaroReady());
        return;
      case Tamaro.TamaroEvent.BeforePaymentSend:
        patchState(this.#store, TamaroInProgress());
        return;
    }
  }

  #handleResultMessage(msg: Tamaro.MessageToHost) {
    switch (msg.name) {
      case Tamaro.Result.EpmsSuccess:
        patchState(this.#store, TamaroDone());
        return;
      case Tamaro.Result.EpmsFailed:
        patchState(this.#store, TamaroFailed({ reason: 'EpmsFailed' }));
        return;
    }
  }

  #emitStateChange(state: State): void {
    this.stateChange.emit(state);

    switch (state) {
      case State.Failed:
        this.failed.emit();
        break;
      case State.Done:
        this.success.emit();
        break;
    }
  }

  #destroyFrame() {
    this.#msgIn$.complete();
    this.#msgOut$.complete();
    this.#frameDestroyed$.next(true);
    this.#frameDestroyed$.complete();
  }

  #setupSentryScope() {
    Sentry.setTag(SentryTamaroTag.Instance, this.instance());
  }

  #cleanupSentryScope() {
    Sentry.setTag(SentryTamaroTag.Instance, undefined);
  }
}
